import { Injectable, Provider } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { UserNotificationListRequest } from '@twrx-api-models';
import { UserNotificationApiService } from 'app/services/api/admin/user-notification-api.service';

import { ActionRequestPayload } from '../action-request-payload';
import { ActionResponsePayload } from '../action-response-payload';

import {
	deleteAllReadUserNotifications,
	deleteUserNotifications,
	loadUnreadCount,
	loadUserNotifications,
	markAllUserNotificationsRead,
	markUserNotificationsRead,
	unreadCountLoaded,
	UserNotificationsActions,
	userNotificationsDeleted,
	userNotificationsDeletedAllRead,
	userNotificationsLoaded,
	userNotificationsMarkedAllRead,
	userNotificationsMarkedRead
} from './user-notifications.actions';

@Injectable()
export class UserNotificationsEffects {
	loadUserNotifications$ = createEffect(() =>
		this.actions.pipe(
			ofType(loadUserNotifications),
			switchMap(
				({
					data,
					cancellationObservable
				}: ActionRequestPayload<UserNotificationListRequest>) =>
					this.userNotificationApiService
						.list(data, cancellationObservable)
						.pipe(
							map(response =>
								userNotificationsLoaded(
									new ActionResponsePayload(response)
								)
							),
							catchError(() =>
								of({
									type: UserNotificationsActions.ErrorOccurred
								})
							)
						)
			)
		)
	);

	loadUnreadCount$ = createEffect(() =>
		this.actions.pipe(
			ofType(loadUnreadCount),
			switchMap(
				({ cancellationObservable }: ActionRequestPayload<void>) =>
					this.userNotificationApiService
						.unreadCount(cancellationObservable)
						.pipe(
							map(count =>
								unreadCountLoaded(
									new ActionResponsePayload(count)
								)
							),
							catchError(() =>
								of({
									type: UserNotificationsActions.ErrorOccurred
								})
							)
						)
			)
		)
	);

	markRead$ = createEffect(() =>
		this.actions.pipe(
			ofType(markUserNotificationsRead),
			switchMap(
				({
					data,
					cancellationObservable
				}: ActionRequestPayload<number[]>) =>
					this.userNotificationApiService
						.markRead(data, cancellationObservable)
						.pipe(
							map(() =>
								userNotificationsMarkedRead(
									new ActionResponsePayload(data)
								)
							),
							catchError(() =>
								of({
									type: UserNotificationsActions.ErrorOccurred
								})
							)
						)
			)
		)
	);

	markAllRead$ = createEffect(() =>
		this.actions.pipe(
			ofType(markAllUserNotificationsRead),
			switchMap(
				({ cancellationObservable }: ActionRequestPayload<void>) =>
					this.userNotificationApiService
						.markAllRead(cancellationObservable)
						.pipe(
							map(() => userNotificationsMarkedAllRead()),
							catchError(() =>
								of({
									type: UserNotificationsActions.ErrorOccurred
								})
							)
						)
			)
		)
	);

	delete$ = createEffect(() =>
		this.actions.pipe(
			ofType(deleteUserNotifications),
			switchMap(
				({
					data,
					cancellationObservable
				}: ActionRequestPayload<number>) =>
					this.userNotificationApiService
						.delete(data, cancellationObservable)
						.pipe(
							map(() =>
								userNotificationsDeleted(
									new ActionResponsePayload(data)
								)
							),
							catchError(() =>
								of({
									type: UserNotificationsActions.ErrorOccurred
								})
							)
						)
			)
		)
	);

	deleteAllRead$ = createEffect(() =>
		this.actions.pipe(
			ofType(deleteAllReadUserNotifications),
			switchMap(
				({ cancellationObservable }: ActionRequestPayload<void>) =>
					this.userNotificationApiService
						.deleteAllRead(cancellationObservable)
						.pipe(
							map(() => userNotificationsDeletedAllRead()),
							catchError(() =>
								of({
									type: UserNotificationsActions.ErrorOccurred
								})
							)
						)
			)
		)
	);

	constructor(
		private readonly actions: Actions,
		private readonly userNotificationApiService: UserNotificationApiService
	) {}
}

export const userNotificationsEffectsProviders: Provider[] = [
	UserNotificationApiService
];
